import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AlertCircle, Check, CornerUpLeft, List, Shuffle } from 'lucide-react';
import { Tooltip } from 'react-tooltip';

import ScorecardNavigation from '../ScorecardNavigation';
import {
  getInteractionRejections,
  getRejectionTypeCounts,
  putRecallVerdict,
  undoRecallVerdict,
  isRejectionReviewType,
  REJECTION_REVIEW_TYPES,
  InteractionRejectionItem,
  RecallVerdict,
  RejectionCoverage,
  RejectionNeighbours,
  RejectionTypeCount,
} from '../../../services/scopedReadsService';
import { prettyType, WINDOW_OPTIONS, DEFAULT_WINDOW, WindowDays } from './interactionEvaluationsQueries';
import { chipHeadlineCount, chipReconciles, deriveEmptyKind, QueueEmptyKind } from './recallCoverage';

/**
 * One tooltip instance for every chip. **It is NOT portaled** — react-tooltip 5 renders
 * where the `<Tooltip>` element is mounted, so what keeps it out of the sticky band is
 * that `<ChipCoverageTooltip>` is mounted at the page root. See the note there.
 */
const CHIP_TOOLTIP_ID = 'recall-chip-coverage';

// ============================================================================
// feat-199 — Recall QA: reviewing what the double-check THREW AWAY.
//
// Spec bucket 3. `interaction_judgement` rows with source='live_double_check' and
// verdict='rejected' — candidates the annotator proposed and its own real-time
// second pass discarded. The rows have existed since feat-156 and no human has ever
// looked at them.
//
// D4 — NOTHING HERE WRITES TO `shift_interaction`. A verdict recorded on this screen
// is eval metadata: no scorecard, no XP total, no leaderboard value moves. The
// screen this one borrows its ergonomics from writes via feat-131's
// `PUT /shift-interactions/{id}`, which is exactly the accident to avoid — see the
// D4 guard test in `scopedReadsService.rejections.test.ts`.
//
// Design: `design/queue.html` rev 3 (design-lead, Preston-ruled). Cards in a
// SCROLLABLE LIST — cards because a feat-199 item is a "unit to consider" (transcript
// + span + claim + a paragraph of prose to read and judge), not a "thing to clear"
// (components.md §feat-185). The spec's "same shape as the feat-159 flashcards" names
// a UI that no longer exists; feat-185 replaced that flashcard with a flush list, so
// continuity is carried by the CONTROLS — click column, verdict language, span
// highlight — and not by the container.
// ============================================================================

/**
 * ADR §0.3 → §0.3.1. The architect ruled NO auto-advance; **Preston OVERRULED it and
 * both auto-advance and `Back` ship.** The overrule carried a binding condition — the
 * write must be debounced or undoable — and §0.3.2 specifies it as an IMMEDIATE write
 * plus a PERSISTENT last-decision strip with Undo.
 *
 * Why immediate-plus-undo rather than a debounce: a debounce opens a durability gap
 * (tab closed inside the window silently loses the decision) and makes AC4's "survives
 * reload" ambiguous about *when*. Undo is strictly better because the write is already
 * an idempotent UPDATE-in-place, so reversing is just another upsert.
 *
 * **The residual is recorded as UNCLOSED, not mitigated** (§0.3.2): undo-via-Back
 * requires her to notice, a debounce requires her to notice faster, and the strip
 * requires her to read it. None closes a hazard whose defining property is that she
 * never learns it happened. Two things reduce it without depending on noticing — the
 * outgoing card carries its verdict as it leaves (below), and `decision_latency_ms`
 * makes the implausibly-fast class visible in the data.
 */
const ADVANCE_ON_DECISION = true;

/**
 * The verdict rail. THREE slots — ADR §1.6(a.1).
 *
 * The mockup drew two and the ADR specified three; escalated rather than picked, and
 * it was ruled in. The third is load-bearing rather than a convenience: **without an
 * escape, a reviewer who genuinely cannot tell is FORCED to guess, and a forced guess
 * enters the overturn rate indistinguishable from a judgement.** That is the exact
 * silent-measurement failure feat-199 exists to end, re-entering through the
 * instrument built to end it. §5's honesty claim is about the DENOMINATOR; this would
 * have been a defect in the NUMERATOR's provenance.
 *
 * `neutral` marks the near-background slot. Per design-lead's measured rule, a
 * selected neutral slot CANNOT signal with fill — selected-fill vs unselected sibling
 * measured **1.08** contrast (invisible, reproducing the feat-185 defect) against
 * **4.19** for the ring. It signals with an EDGE instead. A ring is a box-shadow: it
 * paints outside the layout box, so slot geometry is untouched and N1 holds.
 */
const VERDICT_SLOTS: {
  verdict: RecallVerdict;
  label: React.ReactNode;
  /**
   * feat-205 AC5a — the keyboard letter, held HERE so the binding, the legend and the
   * button can never drift apart. The reference page's grammar is inherited; its
   * LETTERS are not, and that deviation is product-lead's, blessed 2026-08-02:
   * `K` for "**K**ept" collides with move-up, and Shane's daily `J/K` muscle memory
   * outranks a nicer initial. R = the double-check was **R**ight · S = **S**hould have
   * been kept · T = can't **T**ell. No label is renamed — AC4 is untouched.
   */
  key: string;
  neutral?: boolean;
}[] = [
  { verdict: 'upheld', label: <>Double-check<br />was right</>, key: 'r' },
  { verdict: 'overturned', label: <>Should have<br />been kept</>, key: 's' },
  // design-lead's copy: "Can't tell / from this", plainer than "Unsure" for someone
  // working an hour a day who cannot ask a clarifying question.
  { verdict: 'unsure', label: <>Can't tell<br />from this</>, key: 't', neutral: true },
];

/** Letter → verdict, derived from the rail so there is ONE source for the mapping. */
const VERDICT_BY_KEY: Record<string, RecallVerdict> = Object.fromEntries(
  VERDICT_SLOTS.map((s) => [s.key, s.verdict]),
);

/**
 * The legend's gloss per letter. Deliberately NOT the button labels: a legend line
 * wants the shortest phrase that identifies the slot, and the full two-line labels
 * would make the line unreadable. AC4 governs the LABELS, which are untouched.
 */
const SHORTCUT_GLOSS: Record<RecallVerdict, string> = {
  upheld: 'was right',
  overturned: 'should have been kept',
  unsure: "can't tell",
};

/** Past this many transcript lines, expand IN PLACE. Never an internal scroller. */
const COLLAPSED_TRANSCRIPT_ROWS = 10;

const PAGE_SIZE = 25;

/** Non-prod state lever so design-lead can photograph a real render of the real
 *  component. A state you cannot reach is a state that ships broken — and fake mode
 *  only ever reaches the ERROR state (the eval endpoints never short-circuit to dummy
 *  data, despite what scopedReadsService's header used to claim). */
type ForcedState = 'empty' | 'error' | 'end_of_queue' | 'all_suppressed';
const FORCED_STATES: readonly string[] = ['empty', 'error', 'end_of_queue', 'all_suppressed'];
const forcedStateFromQuery = (raw: string | null): ForcedState | null => {
  if (process.env.NODE_ENV === 'production') return null;
  return raw != null && FORCED_STATES.includes(raw) ? (raw as ForcedState) : null;
};

/**
 * feat-205 AC8 — the synthetic coverage envelope behind `?__state=all_suppressed`.
 *
 * **Permitted as a TEST fixture, refused as design evidence** (product-lead,
 * 2026-08-02, reversing an earlier refusal once the two questions were separated).
 * Before Part B lands the backend cannot produce this state at all, so without a lever
 * AC8's third state is unassertable for the whole of Part A. The design-review
 * photograph still comes from real staging data — a lever renders a state the backend
 * cannot yet produce, and it is the lever that ends up in the screenshot.
 *
 * The numbers reconcile (`reviewable_total + suppressed_covered === total_in_scope`)
 * because a fixture that violates the contract it stands in for would train the UI to
 * tolerate a broken one.
 */
const FORCED_ALL_SUPPRESSED_COVERAGE: RejectionCoverage = {
  applied: true,
  // feat-206 D9 — coverage is a transcript-PART intersection now. The lever is what a
  // QA screenshot of this state actually photographs, so a stale label here would get
  // the new state documented under the superseded rule (spec AC29).
  semantics: 'transcript_part_v1',
  retained_definition: 'row_exists_and_not_human_marked_incorrect',
  // **DELIBERATELY ROUND AND OBVIOUSLY SYNTHETIC — and this is the settled position,
  // arrived at twice.** The real figures live in
  // `reference/MEASUREMENT-2026-08-10-containment.md` (7,194 candidates; 605 with an
  // empty part set; 364 zero-length, 241 positive-length inside one part). **That file
  // is where a reader goes for real numbers. These are not them.**
  //
  // **Why measured components are the WRONG choice here, which is not obvious and was
  // briefly ruled the other way:** this lever renders a state the production data does
  // not contain — nothing is ever all-suppressed. Real numbers arranged to depict an
  // impossible state are the worst available combination, because they are *internally
  // consistent and wrong about the world*, which is exactly what lets a fabricated
  // figure survive scrutiny. This constant has had that failure before (see 1,134
  // below). A round number announces itself; `1,011` or `364` invites a re-quote.
  //
  // A brief re-pin to the measured 364/364/0/364 was reverted for a second reason worth
  // recording: it rendered "364 of those 364" — a degenerate 100 % that **does not
  // exercise the "of which" relationship the block exists to express**, so it
  // demonstrated less as a QA artifact than the synthetic version does.
  //
  // (History, kept because each entry is a different way to be wrong: 1,134 came from a
  // 61.4 % figure that reconciled internally and was wrong about the world. 927 was the
  // real post-epsilon Phase-1 covered count, 1,904 × 48.7 %, measured 2026-08-03 — real,
  // but a measurement of a rule that no longer runs. 1,011/118 was design's mockup
  // figure, invented but shaped like a reading.)
  total_in_scope: 1000,
  suppressed_covered: 1000,
  reviewable_total: 0,
  null_span_reviewable: 0,
  // Non-zero AND a proper subset, on purpose. The fixture exists so a state can be
  // photographed, this line is the new element in it, and `120 of those 1,000` shows the
  // subset relationship that `364 of 364` collapses.
  suppressed_zero_length: 120,
};

const parseWindow = (raw: string | null): WindowDays => {
  const n = Number(raw);
  return (WINDOW_OPTIONS as readonly number[]).includes(n) ? (n as WindowDays) : DEFAULT_WINDOW;
};

// ---------------------------------------------------------------------------
// Card sub-surfaces
// ---------------------------------------------------------------------------

/**
 * The transcript window, with the span marked by ONE coral rule on the BLOCK rather
 * than per row. Post-feat-183 the returned window IS the scored span, so a per-row
 * rule would mark ~100% of rows — one block rule is what "this block is the scored
 * span" literally means now, at an eighth of the ink.
 *
 * `window_status` is honoured because an empty transcript has MORE THAN ONE CAUSE and
 * rendering them alike is AC10 one level below the page — the card still *looks*
 * complete either way, which is what makes it dangerous.
 */
const TranscriptWindow: React.FC<{ item: InteractionRejectionItem }> = ({ item }) => {
  const parts = item.transcript ?? [];
  const [showAll, setShowAll] = useState(false);
  const overflow = parts.length - COLLAPSED_TRANSCRIPT_ROWS;
  const visible = showAll ? parts : parts.slice(0, COLLAPSED_TRANSCRIPT_ROWS);

  if (item.window_status === 'no_anchor') {
    return (
      <div
        data-testid="window-status"
        data-window-status="no_anchor"
        className="mb-5 border-l-2 border-warning-solid bg-warning-surface/40 py-3 pl-3"
      >
        <p className="text-xs font-semibold">No anchor to build a window from</p>
        <p className="mt-0.5 text-[11px] leading-relaxed">
          The snapshot has no usable start/end. <strong>We couldn't show you the context</strong> —
          this is a data gap, not an empty conversation.
        </p>
      </div>
    );
  }

  if (item.window_status === 'empty_window' || parts.length === 0) {
    return (
      <div
        data-testid="window-status"
        data-window-status="empty_window"
        className="mb-5 border-l-2 border-softBorder bg-cream/40 py-3 pl-3"
      >
        <p className="text-xs font-semibold text-inkMuted">Window built, no transcript in it</p>
        <p className="mt-0.5 text-[11px] leading-relaxed text-inkMuted">
          The span resolved but contains no parts.{' '}
          <strong className="text-ink">Judge on the claim and the reason alone</strong>, or mark
          “Can't tell from this”.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="window-status"
      data-window-status="ok"
      className="mb-5 border-l-2 border-coral bg-cream/60 py-2 pl-3"
    >
      {visible.map((p, idx) => (
        <p key={`${p.start_time}-${idx}`} data-testid="transcript-row" className="text-[13px] leading-[18px]">
          {p.text}
        </p>
      ))}
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((s) => !s)}
          className="mt-1 text-[11px] text-coral hover:underline"
        >
          {showAll ? 'Show fewer lines' : `+${overflow} more lines`}
        </button>
      )}
    </div>
  );
};

/**
 * feat-206 — **this card is in a DEFECT state: something the annotator kept shares a
 * transcript part with it, so the coverage predicate should have suppressed it.**
 *
 * Reachable only if the row-selection predicate and the neighbour annotation disagree
 * (AC27(a) forbids that by construction — they come from one SQL fragment), so in a
 * healthy system this is always `false`. That is what makes it worth having: it is the
 * runtime detector for the two implementations drifting apart, and the day it fires is
 * the day nothing else notices.
 *
 * Used for two things that must agree — the per-row note, and the write guard. One
 * predicate, one place, on the same reasoning that deleted the client-side coverage
 * derivation in the first place.
 */
const hasSharingNeighbour = (item: InteractionRejectionItem): boolean =>
  (item.neighbours?.items ?? []).some((n) => n.shares_part === true);

const clock = (s: number | null | undefined): string => {
  if (s == null) return '—';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s - m * 60)).padStart(2, '0')}`;
};

/**
 * feat-205 AC16 / D7 — "what the annotator kept near this span", inside the ONE
 * disclosure.
 *
 * The rendering decision that matters here is not the list, it is the **why-this-card-
 * is-still-here** line, and feat-206 AC28 changed where it comes from.
 *
 * **THE CLIENT DOES NOT DECIDE COVERAGE.** It used to: `const COVERAGE_EPSILON_S = 0.5`
 * lived here and `sliver` / `touching` / `covers` were derived from it, one of which
 * rendered "this candidate would be suppressed today" — **a second implementation of
 * the server's predicate, running in the browser.** That is the failure documented at
 * `routes/scoped_reads.py:389-401`: the emitter said `half_open` while `epsilon_0.5s`
 * ran, both suites were green, they asserted opposite values, and nothing compared
 * them. Under `transcript_part_v1` the arithmetic is not merely stale: **no function of
 * `overlap_seconds` and `gap_seconds` decides part containment**, because whether a part
 * STARTS inside a span is a fact about the transcript, not about the two durations. So
 * it was deleted rather than retuned. It is NOT replaced by a different client-side
 * derivation: every per-row verdict is `shares_part`, straight off the wire, computed
 * by the same SQL fragment that decided whether this card is in the queue at all.
 *
 * Two things survive, and both for the same reason — they read a server FLAG rather
 * than deriving anything:
 *
 *  - `zero_length` — a POINT, not a span. It contains no transcript part, so it cannot
 *    cover, structurally. **More** true under the new rule than under the old one,
 *    where it merely failed a threshold.
 *  - the falsifiability argument itself: a reviewer who keeps seeing cards whose
 *    neighbours look like real cover is still the person who can tell us the rule is
 *    wrong. It now rests on a block-level invariant plus the server's own per-row
 *    verdict, instead of on client arithmetic that could disagree with the server
 *    silently.
 */
const NeighbourList: React.FC<{ neighbours: RejectionNeighbours }> = ({ neighbours }) => {
  if (neighbours.items.length === 0) {
    return (
      <div
        data-testid="neighbours"
        data-neighbour-count="0"
        className="rounded-lg border border-softBorder bg-white p-2.5 text-[12px] text-inkMuted"
      >
        <p className="font-extrabold text-ink">Nothing else was kept near this span</p>
        {/* A measurement, not a silence: the backend looked within the radius and
            found none. Distinguished from the pre-Part-B case, which renders no
            section at all. */}
        <p className="mt-0.5">
          The annotator kept no other interaction within {neighbours.radius_seconds}s — so nothing
          here suggests the moment survived under another type.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="neighbours"
      data-neighbour-count={neighbours.items.length}
      className="rounded-lg border border-preview/30 bg-preview-light p-2.5"
    >
      <p className="text-[12px] font-extrabold text-preview">What the annotator kept near this span</p>
      <ul className="mt-1 space-y-1">
        {neighbours.items.map((n) => {
          // ZERO-LENGTH INTERACTION — a POINT, not a span, and the DOMINANT case: 579 of
          // the 938 old sub-millisecond pairs (61.7 %) came from these rather than from
          // FLOAT promotion, and 183 candidates were suppressed by points alone. It
          // keeps its own branch under the new rule because it is the one thing a
          // neighbour can be that the reviewer can act on: a point contains no
          // transcript part, so it cannot cover anything — by construction, not by
          // failing a threshold.
          //
          // **BRANCH ON THE SERVER'S FLAG, not on `start_time === end_time`.** The float
          // comparison works today only because both endpoints of one row truncate
          // identically on the wire — it works by a coincidence in the rendering, which
          // is exactly what the truncation guard exists to prevent. Deliberately NO
          // fallback to the comparison: a fallback keeps the coincidence alive and hides
          // the day the flag stops arriving.
          //
          // `=== true` is STRICT on purpose, and the tradeoff is worth stating. The
          // backend emits a real JSON `true`/`false` and pins that with a test, because
          // MySQL's `1`/`0` would fail a strict check silently and render EVERY point as
          // "not a point". A truthy test here would absorb that regression instead of
          // exposing it — so the strictness is deliberate, and the guard against `1`
          // lives on the emitting side where it can actually be asserted.
          const zeroLength = n.zero_length === true;
          // THE SERVER'S VERDICT, NOT OURS (feat-206 AC27/AC28). Same SQL fragment as
          // the row-selection predicate, so this line and the decision to show the card
          // at all cannot drift apart.
          //
          // **ABSENT IS NOT FALSE.** An older backend does not send this field, and
          // `?? false` would convert "this response cannot answer" into "shares no
          // transcript part" — a claim nobody made, rendered in the reviewer's own
          // words. `null` renders no per-row line at all; the block invariant below is
          // what still holds in that state.
          const sharesPart = typeof n.shares_part === 'boolean' ? n.shares_part : null;
          // **`shares_part` BRANCHES FIRST; `zero_length` is a sub-label inside its
          // false branch** (design, 2026-08-09, correcting my first ordering). I had
          // `zero_length` winning on the grounds that it is more specific and still
          // true — and "still true" was the part that was open. If a zero-length
          // neighbour ever arrives with `shares_part: true`, the old order would print
          // "it contains no transcript part" directly beside a wire field saying it
          // shares one, and would not flag the row as a cover: the card mis-explaining
          // itself silently, which is the S3 finding rebuilt from the other side.
          //
          // Under the revised D5 (half-open start-time containment) a zero-length span
          // selects nothing, so `zero_length` now implies `shares_part === false` and
          // the two orderings agree in practice. **Keep this one anyway** — it means
          // the row cannot contradict the wire whatever the predicate does next, which
          // is the entire point of not re-deriving it here.
          const notCoverReason =
            sharesPart === false ? (zeroLength ? 'zero_length' : 'no_shared_part') : null;
          // **UNREACHABLE ON EVERY CARD ON THIS SURFACE, not just an undecided one —
          // and that is the point of rendering it.** An earlier version of this comment
          // (mine, carried over from the feat-205 text) said it "only appears on one
          // decided before this rule shipped". **That premise is false and was measured
          // false 2026-08-10 by team-lead, in the shipped backend:**
          // `_rejection_population_sql` (`routes/scoped_reads.py:489-501`) has NO
          // review-status join and no decided-row branch, and the queue appends
          // `AND NOT <predicate>` to exactly that population. So a decided card is
          // suppressed on the same terms as an undecided one; decidedness never bought
          // an exemption, and a neighbour that shares a part cannot accompany any card
          // that survived the filter.
          //
          // Which makes this branch **the runtime guard on AC27(b)**, not dead code. If
          // the row-selection predicate and the neighbour annotation ever diverge, every
          // other instrument stays quiet: the SQL is self-consistent, both suites are
          // green, and the fixture-based invariant test still passes — while the screen
          // tells a human something false. This is the one thing in either repo that
          // notices at the moment it matters, and it fails to SILENCE (the block
          // invariant is withdrawn) rather than to a lie. **Do not delete it as
          // unused.**
          const covers = sharesPart === true;
          return (
            <li
              key={n.interaction_id}
              data-testid="neighbour-row"
              data-shares-part={sharesPart == null ? 'unknown' : String(sharesPart)}
              data-zero-length={zeroLength ? 'true' : 'false'}
              // Proximity facts, still true and still the server's — they no longer
              // decide anything, and no code reads them back to classify a row.
              data-overlap-seconds={n.overlap_seconds}
              data-gap-seconds={n.gap_seconds}
              // SLATE, not amber — design-lead's axis, and the reason is worth keeping:
              // **amber means a surprise nobody chose; slate means a decision the system
              // made correctly.** The rule deliberately returns these cards, so nothing
              // is wrong here — nothing kept shares a part. Amber would read as
              // "something is off", and it would spend the one hue that has to stay
              // meaningful for states that genuinely need attention. Prominence comes
              // from an EDGE instead, which is the same ring-not-fill discipline as the
              // verdict rail.
              className={`text-[12px] ${
                notCoverReason ? 'rounded border-l-2 border-inkMuted bg-white px-1.5 py-1' : ''
              } ${covers ? 'rounded border-l-2 border-danger-line bg-danger-surface px-1.5 py-1' : ''}`}
            >
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[11px] text-preview">{clock(n.start_time)}</span>
                <span className="rounded bg-white px-1 text-[11px] font-semibold">
                  {prettyType(n.interaction_type)}
                </span>
                <span className="min-w-0 flex-1 truncate text-inkMuted">
                  {n.excerpt ? `“${n.excerpt}”` : <span className="italic">no excerpt</span>}
                </span>
              </span>
              {notCoverReason && (
                <span
                  data-testid="neighbour-not-cover-reason"
                  data-reason={notCoverReason}
                  className="mt-0.5 block text-[11px] font-semibold text-inkMuted"
                >
                  {notCoverReason === 'zero_length' ? (
                    // Revised 2026-08-09. The retired version ended "It contains no
                    // transcript part, so it cannot cover anything" — an INFERENCE
                    // about the rule, and one of the three sentences contingent on the
                    // `parts()` semantics ruling. With `shares_part` on the wire the
                    // mechanism no longer has to be inferred: the server's answer
                    // carries the clause, and this sentence is true under either
                    // semantics.
                    <>
                      <strong>A detection with no duration</strong> — and it shares no transcript
                      part with this candidate, so it did <strong>not</strong> suppress this card.
                    </>
                  ) : (
                    <>
                      <strong>Shares no transcript part</strong> with this candidate, so it did{' '}
                      <strong>not</strong> suppress this card.
                    </>
                  )}
                </span>
              )}
              {covers && (
                // **REPLACED 2026-08-10 — the old sentence explained a BUG as history.**
                // It ended "you are seeing it because the call was recorded before this
                // rule shipped", written when a decided card was believed exempt from
                // suppression. It is not (see the `covers` note above), so the only
                // state that renders this block is the predicate and the neighbour
                // annotation disagreeing — and telling a reviewer that a defect is a
                // routine historical artifact, on the one screen whose entire job is
                // catching what the machine got wrong, is the same species of false
                // mechanism AC28 deleted. team-lead ruled to ship the true version
                // immediately rather than wait on a copy pass; design supplied the
                // wording and caught that the version I proposed named a control that
                // does not exist ("flag it") — the card has three verdict buttons and
                // Back, nothing else.
                //
                // **DANGER TOKENS, NOT `preview`.** By this block's own axis — amber is
                // a surprise nobody chose, slate is a decision the system made
                // correctly — slate is backwards here: nothing was decided correctly,
                // two implementations of one predicate disagree. Not amber either;
                // that is "attention, not alarm" for an incomplete model, and this is
                // broken for certain. `danger` is what these surfaces already use for a
                // contract violation shown to a user (`suppression-unreconciled`,
                // `covered-decisions-missing`), so this is precedent, not taste.
                <span
                  data-testid="neighbour-covers-note"
                  className="mt-0.5 block text-[11px] font-semibold text-danger-text"
                >
                  <strong>Shares a transcript part</strong> with this candidate —{' '}
                  <strong>so this card should not be in your queue.</strong> That is a fault in the
                  rule, not a judgement about this moment. Deciding is disabled here; report this
                  card rather than working around it.
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {/* The block-level invariant — the strongest thing that is still true about every
          row here, and it comes from the card's own presence rather than from any
          number on it. **It is the one line that must never be false**, and it is
          withdrawn in exactly ONE state:

          **Any neighbour reports `shares_part: true`** — then it is false on its face.
          Unreachable while the backend behaves (see the `covers` note above), which is
          precisely why it earns its lines: it is the runtime guard on AC27(b), and the
          only instrument that would notice the row-selection predicate and the neighbour
          annotation drifting apart. Deleting it as unused deletes the detection, not the
          case.

          **A second condition lived here and was removed 2026-08-10 — the removal is
          worth more than the code was.** It withheld the invariant on an already-DECIDED
          card whose neighbours reported no `shares_part`, on the theory that "This card
          is in your queue" could not be checked for such a card. team-lead then measured
          the shipped backend: `_rejection_population_sql` has no review-status join and
          no decided-row branch, so **the coverage predicate applies uniformly and a
          decided card's queue membership vouches for the invariant exactly as an
          undecided card's does.** The premise was false, and keeping code whose stated
          justification is false is the defect class this feature has now produced four
          times — the fact that it erred safe does not redeem the reasoning attached to
          it.

          It does not survive as insurance either, and the argument is worth keeping
          because it is not obvious: if some future backend DID exempt decided rows, a
          decided-but-covered card would appear here — and its covering interaction has
          `gap_seconds == 0` (sharing a part forces a positive-length intersection), so
          the boundary-preserving cap in `_fetch_neighbours` guarantees it is IN the
          list, reporting `shares_part: true`. **Condition 1 fires and the invariant is
          withheld correctly, with no decidedness involved.** The removed condition
          insured against nothing, present or future.

          A `!neighbours.truncated` clause went with it, for the same kind of reason: it
          guarded a truncation exposure that the cap already closes — see
          {@link RejectionNeighbours.items} and AC32.

          When it IS withdrawn, the covers note becomes the only line carrying why the
          card is here — which is why that note renders inline and is never collapsed or
          truncated. */}
      {!neighbours.items.some((n) => n.shares_part === true) && (
        <p
          data-testid="neighbours-invariant"
          className="mt-2 rounded border-l-2 border-inkMuted bg-white px-2 py-1.5 text-[11px] leading-relaxed text-inkMuted"
        >
          This card is in your queue, so{' '}
          <strong className="text-ink">
            nothing the annotator kept shares a transcript part with it
          </strong>{' '}
          — including everything listed here. These are what was kept nearby, for context.
        </p>
      )}
      {/* "the **nearest** N" rests on a property that is TRUE today and contracted
          nowhere: the ordering of the non-boundary remainder the cap keeps. See
          {@link RejectionNeighbours.items} — including the reworded fallback to use if
          AC32 does not pin it. Pre-existing (feat-205), not introduced here, and not a
          defect: the sort is nearest-first in the shipped backend. */}
      {neighbours.truncated && (
        <p data-testid="neighbours-truncated" className="mt-1.5 text-[11px] font-semibold text-inkMuted">
          Showing the nearest {neighbours.items.length} — there are more within{' '}
          {neighbours.radius_seconds}s.
        </p>
      )}
    </div>
  );
};

/**
 * The annotator's pipeline: what was claimed, what the double-check was GIVEN, and
 * why it discarded.
 *
 * `candidate_excerpt` and the transcript are rendered as DIFFERENT things on purpose
 * (ADR §3.1). `candidate_excerpt` is the EXACT string handed to the model; the
 * transcript window is a RECONSTRUCTION and is not provably identical to it. A
 * reviewer judging "was the double-check right?" from text the double-check was never
 * shown is answering a different question — confidently.
 */
const AnnotatorPipeline: React.FC<{ item: InteractionRejectionItem }> = ({ item }) => (
  <div data-testid="ai-evidence" className="mt-3 space-y-2.5 rounded-xl bg-cream p-3 text-[12.5px] leading-relaxed">
    {/*
      feat-205 AC1/AC2 — this block used to be the card's FACE, above the transcript,
      with four section headings. It is now the entire contents of ONE disclosure.
      The section heading is gone with it: inside a control already labelled
      "AI evidence", a second "Annotator's own pipeline" header is the density the
      feature exists to remove.
    */}
    {/*
      AC2 names FOUR things on the card and this is the one I had dropped: **the
      annotator's CLAIM**. Without it the card shows what the double-check was given
      and why it refused, but never states what was being asserted — so the reviewer
      is judging a rebuttal with no motion in front of it.

      Derived, not fabricated. There is no "claim" field on the wire and there doesn't
      need to be: a `rejected` row IS the annotator asserting that an interaction of
      this type occurred over this span. The type is on the item; the span is what the
      transcript block marks. Stating it plainly is the honest rendering of what the
      row means, and it is what the mockup draws.
    */}
    <div className="mb-3">
      <p className="text-xs font-bold text-ink">The annotator claimed</p>
      <p className="mt-0.5 text-sm leading-relaxed" data-testid="annotator-claim">
        A <strong>{prettyType(item.interaction_type)}</strong> occurred
        {item.candidate_excerpt ? <> — “{item.candidate_excerpt}”</> : null}.
      </p>
    </div>
    <div className="mb-3">
      <p className="text-xs font-bold text-ink">What the double-check was given</p>
      <p
        data-testid="candidate-excerpt"
        className="mt-0.5 rounded bg-cream/70 px-2 py-1 font-mono text-[12px] leading-relaxed text-inkMuted"
      >
        {item.candidate_excerpt || <span className="italic">No excerpt recorded.</span>}
      </p>
    </div>
    <div>
      <p className="text-xs font-bold text-ink">The live double-check discarded it because</p>
      <blockquote
        data-testid="discard-reason"
        className="mt-0.5 border-l-2 border-warning-solid/50 bg-warning-surface/40 py-1.5 pl-3 text-sm leading-relaxed"
      >
        {item.reasoning || <span className="italic text-inkMuted">No reasoning recorded.</span>}
      </blockquote>
    </div>
    {/* AC16 / D7 — absent entirely before Part B. An empty <NeighbourList/> would
        assert "nothing was kept here", which is a measurement we do not have. */}
    {item.neighbours && <NeighbourList neighbours={item.neighbours} />}
    {/* AC2's identifiers. They were the FIRST thing on the old card face; they are the
        LAST thing inside the disclosure, because they are what you need only once you
        have decided to investigate. */}
    <div
      data-testid="pipeline-identifiers"
      className="flex flex-wrap gap-x-4 gap-y-1 border-t border-softBorder pt-2 font-mono text-[11px] text-inkMuted"
    >
      <span>shift {item.shift_id}</span>
      {item.prompt_version_id && <span>prompt {item.prompt_version_id}</span>}
      <span>judgement {item.judgement_id}</span>
      <span>
        span {clock(item.start_time)}–{clock(item.end_time)}
      </span>
    </div>
  </div>
);

/**
 * The click column. Fixed `w-[150px]` right gutter (design-lead's approved mockup —
 * was `w-[124px]`; the width changed, the invariant did not) so every card's controls
 * sit at the same x and step down the page predictably.
 *
 * **N1 (feat-185, learned the hard way): selection changes FILL, never WIDTH.** A
 * `flex-1` on the selected slot moved its siblings and made targets zigzag — measured
 * a 55.5px jump. Selection is signalled with a RING, which is a box-shadow: it paints
 * outside the layout box, so slot geometry is untouched regardless of state.
 *
 * feat-205 changes the IDLE treatment, not the rule: the two substantive slots now
 * carry their tone as fill (success / danger, per the mockup) instead of being
 * uniformly cream. The neutral slot still cannot signal with fill — selected-fill vs
 * unselected sibling measured **1.08** contrast (invisible, the feat-185 defect)
 * against **4.19** for the ring — so every slot signals selection with the edge.
 * Under AC3 a decided card collapses, so a selected rail is only visible on a REOPENED
 * card, which is exactly when the reviewer needs to see what they previously chose.
 */
const ClickColumn: React.FC<{
  verdict: RecallVerdict | null;
  busy: boolean;
  onDecide: (v: RecallVerdict) => void;
  onBack?: () => void;
  backDisabled?: boolean;
  /**
   * feat-206 — the card is in the defect state (see {@link hasSharingNeighbour}), so
   * deciding is refused. A SECOND flag through the existing `disabled` path rather than
   * new machinery, and deliberately NOT folded into `busy`: `busy` means a write is in
   * flight and clears on its own, while this does not clear at all.
   *
   * The buttons are the visible half; the write guard in `decide` is the load-bearing
   * half, because the keyboard shortcuts never touch these elements.
   */
  blocked?: boolean;
}> = ({ verdict, busy, onDecide, onBack, backDisabled, blocked }) => (
  <div className="flex w-[150px] shrink-0 flex-col gap-2" data-testid="click-column">
    {/* **NEVER LEAVE A DISABLED CONTROL UNEXPLAINED** (design). The full statement is
        in the neighbour block — which lives behind the AI-evidence disclosure, so on a
        closed card it is not on screen. This line travels with the control itself. */}
    {blocked && (
      <p
        data-testid="verdict-blocked"
        className="rounded-lg border-l-2 border-danger-line bg-danger-surface px-2 py-1.5 text-[11px] font-semibold leading-relaxed text-danger-text"
      >
        Deciding is disabled — this card should not be in your queue. Open{' '}
        <strong>AI evidence</strong> for why, and report it.
      </p>
    )}
    {VERDICT_SLOTS.map((slot) => {
      const selected = verdict === slot.verdict;
      // Static literal class strings — never interpolated. Tailwind purges
      // constructed class names and the render fails SILENTLY (unstyled).
      const base =
        'w-full rounded-xl px-3 py-2.5 text-left text-[13px] font-bold leading-tight transition-colors disabled:opacity-50';
      const tone = slot.neutral
        ? 'border border-softBorder bg-white text-inkMuted'
        : slot.verdict === 'upheld'
        ? 'bg-success text-white'
        : 'bg-danger text-white';
      // The EDGE, not a fill. Ring + offset so it reads against a filled slot too.
      const chosen = 'ring-2 ring-ink ring-offset-1';
      return (
        <button
          key={slot.verdict}
          type="button"
          disabled={busy || blocked === true}
          aria-pressed={selected}
          data-testid={`verdict-${slot.verdict}`}
          onClick={() => onDecide(slot.verdict)}
          className={`${base} ${tone} ${selected ? chosen : ''}`}
        >
          {slot.label}{' '}
          <span className="float-right font-mono text-[11px] opacity-70" aria-hidden="true">
            {slot.key.toUpperCase()}
          </span>
        </button>
      );
    })}
    {onBack && (
      <button
        type="button"
        onClick={onBack}
        disabled={backDisabled}
        data-testid="back-button"
        className="mt-1 w-full rounded-lg border border-softBorder px-2 py-1.5 text-[11px] font-semibold text-inkMuted hover:bg-cream disabled:opacity-40"
      >
        ← Back
      </button>
    )}
  </div>
);

/**
 * The last-decision strip — ADR §0.3.2's specified control, on the INCOMING card
 * where her eyes already are after an advance.
 *
 * Three properties are load-bearing, not styling:
 *  1. **It names the VERDICT, not "Saved ✓".** A generic confirmation makes a
 *     mis-click legible as *something happened*; the verdict makes it legible as
 *     *wrong*. Only the second is a control.
 *  2. **It PERSISTS — no auto-dismiss timer.** A timed toast is a caption with a
 *     timer: present at the moment of authoring, absent at the moment of need. It
 *     stays until the next decision replaces it.
 *  3. **After an Undo it does NOT clear.** If it cleared, the only evidence a
 *     write-and-unwrite happened would vanish — and an Undo can itself be a mis-click.
 *     The copy says the row is GONE rather than set to something neutral, because the
 *     clear-not-a-stand-in decision is otherwise invisible from the UI.
 */
const LastDecisionStrip: React.FC<{
  state: { shiftId: string; verdict: RecallVerdict; undone: boolean };
  busy: boolean;
  onUndo: () => void;
}> = ({ state, busy, onUndo }) => {
  const phrase =
    state.verdict === 'upheld'
      ? '“the double-check was right”'
      : state.verdict === 'overturned'
      ? '“should have been kept”'
      : '“can’t tell from this”';
  // design-lead: the VERB changes for the third verdict — she didn't assert
  // anything, she declined to.
  const verb = state.verdict === 'unsure' ? 'marked' : 'said';

  return (
    <div
      data-testid="last-decision-strip"
      data-undone={state.undone ? 'true' : 'false'}
      className="-mt-1 mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-softBorder bg-cream/70 px-3 py-2 text-xs"
    >
      <CornerUpLeft className="h-3.5 w-3.5 shrink-0 text-inkMuted" aria-hidden="true" />
      {state.undone ? (
        <span className="text-inkMuted">
          Undone — Shift <span className="font-semibold text-ink">{state.shiftId}</span> is back to{' '}
          <strong className="text-ink">undecided</strong>, as if you'd never answered it.
        </span>
      ) : (
        <>
          <span className="text-inkMuted">
            Last card — you {verb} <strong className="text-ink">{phrase}</strong> on Shift{' '}
            <span className="font-semibold text-ink">{state.shiftId}</span>
          </span>
          <button
            type="button"
            disabled={busy}
            onClick={onUndo}
            data-testid="undo-button"
            className="rounded border border-softBorder bg-white px-2 py-0.5 font-bold text-coral hover:bg-coral-light disabled:opacity-50"
          >
            Undo
          </button>
        </>
      )}
    </div>
  );
};

/**
 * The type picker — and the reason it shows COUNTS rather than just names.
 *
 * There are FOUR reasons a type can show nothing, and **two of them are both "empty
 * and intended"**: `deferred` (rejections exist, deliberately out of Phase 1) and
 * `cannot_be_rejected` (auto-confirmed, impossible). Styling cannot separate those
 * honestly — **the only thing that does is the number.** A reviewer looking at a
 * chip with no count cannot tell "nothing here" from "thousands here, deliberately
 * not shown to you", which is Eric's founding question (*"I don't know what I'm not
 * looking at"*) reappearing on the queue built to answer it.
 *
 * So: the count renders ON the chip, never a click away, and each state gets its own
 * copy. The FOURTH state (`not_in_phase_1_scope` — a type in the data that this
 * frontend has never heard of) is rendered as an explicit unknown WITH its count
 * rather than dropped, because a type nobody enumerated carrying real rejections is
 * the strongest instance of the thing this feature exists to surface.
 */
/**
 * FOUR states, FOUR tokens — and design-lead ruled that grouping them by "there is
 * something here you aren't seeing" was the error, because it hides the axis that
 * decides the treatment:
 *
 *  - **`deferred` is a DECISION.** Preston chose it. Nothing is wrong; it is PARKED.
 *    So: neutral, no alarm colour — **the COUNT does the work.**
 *  - **`not_in_phase_1_scope` is a SURPRISE.** A type carries real rejections and this
 *    frontend has never enumerated it. Nobody chose that; it is NEWS, and per feat-174
 *    the warning token means *attention, not alarm* — a review signal. An unrecognised
 *    type carrying real rows says **our model of the type space is incomplete**, which
 *    is exactly that. It does NOT appear on this screen though — see below.
 *  - **`cannot_be_rejected` uses `preview-*`**, which means *not-real data*. Only the
 *    structural case qualifies: there is no data and there never can be.
 *
 * Copy is deliberately NOT engineering vocabulary. Shane has no reason to know what
 * "Phase 1" is, and "not recognised by this screen" describes our software rather
 * than her work. "Deferred from Phase 1" survives only on the metrics panel, whose
 * audience made the decision.
 */
const TYPE_STATE_COPY: Record<
  string,
  { note: (n: number) => string; className: string }
> = {
  in_phase_1: { note: () => '', className: '' },
  deferred: {
    note: (n) => `${n.toLocaleString()} waiting`,
    className: 'border-softBorder bg-white text-ink',
  },
  cannot_be_rejected: {
    note: () => 'always auto-confirmed — never discarded',
    className: 'border-preview/30 bg-preview-light text-preview',
  },
  // Rendered on the METRICS panel, not here. Kept in the map so an unexpected
  // `reviewable: true` on this state still gets a defined treatment rather than
  // falling through to a default that means something else.
  not_in_phase_1_scope: {
    note: (n) => `${n.toLocaleString()} rejections · not covered by this screen`,
    className: 'border-warning-on/40 bg-warning-surface text-warning-on',
  },
};

const TypePicker: React.FC<{
  counts: RejectionTypeCount[] | null;
  countsFailed: boolean;
  current: string;
  windowDays: WindowDays;
}> = ({ counts, countsFailed, current, windowDays }) => {
  // Fall back to the known reviewable types so the picker still works — but WITHOUT
  // counts. Rendering "0" here would assert a measurement we do not have, which is
  // the AC10 failure one level in: a fetch that failed must never render as a zero.
  const rows: RejectionTypeCount[] =
    counts ??
    REJECTION_REVIEW_TYPES.map((t) => ({
      interaction_type: t,
      rejections: -1,
      state: 'in_phase_1',
      reviewable: true,
    }));

  // design-lead's ruling: the unaccounted-type state is IMPORTANT but on the WRONG
  // SURFACE here. Shane cannot act on it — she can't review a type the screen doesn't
  // understand — so as a chip it is unactionable noise in a control built for one
  // decision per card. Same reconciliation as drink_promotion: HIDE THE DEAD END FROM
  // THE PIECE-WORKER, STATE IT FOR THE NUMBERS AUDIENCE. She learns it exists; she is
  // not invited to click it. It is a first-class row on the metrics panel instead.
  const unaccounted = rows.filter((r) => r.state === 'not_in_phase_1_scope');
  const chips = rows.filter((r) => r.state !== 'not_in_phase_1_scope');
  const unaccountedTotal = unaccounted.reduce((n, r) => n + Math.max(0, r.rejections), 0);

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="type-picker">
      {chips.map((row) => {
        const copy = TYPE_STATE_COPY[row.state] ?? TYPE_STATE_COPY.not_in_phase_1_scope;
        const selected = row.interaction_type === current;
        const countLabel = row.rejections >= 0 ? row.rejections.toLocaleString() : '—';

        if (!row.reviewable) {
          return (
            <span
              key={row.interaction_type}
              data-testid={`type-chip-${row.interaction_type}`}
              data-state={row.state}
              data-count={row.rejections}
              title={copy.note(row.rejections)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${copy.className}`}
            >
              {prettyType(row.interaction_type)}
              <span className="rounded-full bg-white/60 px-1.5 py-0.5 text-[10px] font-bold">
                {countLabel}
              </span>
              <span className="text-[10px] font-normal">{copy.note(row.rejections)}</span>
            </span>
          );
        }

        // D10 / AC9 — the HEADLINE is the REVIEWABLE count once Part B supplies one.
        // At ~50 % suppression the gap between reviewable and total is the single most
        // misleading thing a chip could hide: it is a promise about Shane's workload.
        const headline = row.rejections >= 0 ? chipHeadlineCount(row) : -1;
        const headlineLabel = headline >= 0 ? headline.toLocaleString() : '—';
        const hasCoverage = row.reviewable_rejections != null && row.covered_suppressed != null;
        return (
          <Link
            key={row.interaction_type}
            to={`/restaurant/internal/interaction-evaluations/rejections/${row.interaction_type}?days=${windowDays}`}
            data-testid={`type-chip-${row.interaction_type}`}
            data-state={row.state}
            data-count={row.rejections}
            data-headline-count={headline}
            data-tooltip-id={hasCoverage ? CHIP_TOOLTIP_ID : undefined}
            data-chip-type={hasCoverage ? row.interaction_type : undefined}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${
              selected
                ? 'bg-coral text-white'
                : 'border border-softBorder bg-cream text-ink hover:bg-cream-dark'
            }`}
          >
            {prettyType(row.interaction_type)}
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                selected ? 'bg-white/25' : 'bg-cream-dark'
              }`}
            >
              {headlineLabel}
            </span>
            {hasCoverage && (
              <span
                aria-hidden="true"
                className={`grid h-4 w-4 place-items-center rounded-full text-[10px] font-extrabold leading-none ${
                  selected ? 'bg-white/25' : 'bg-cream-dark text-inkMuted'
                }`}
              >
                i
              </span>
            )}
          </Link>
        );
      })}
      {unaccounted.length > 0 && (
        <p
          data-testid="unaccounted-types-note"
          className="w-full text-[11px] text-inkMuted"
        >
          {unaccounted.length} other type{unaccounted.length === 1 ? '' : 's'} ha
          {unaccounted.length === 1 ? 's' : 've'} rejections this screen doesn't cover (
          {unaccountedTotal.toLocaleString()}). Shown on the metrics panel.
        </p>
      )}
      {countsFailed && (
        <span
          data-testid="type-counts-failed"
          className="text-[11px] font-semibold text-danger-text"
        >
          Counts didn't load — these aren't zeros.
        </span>
      )}
    </div>
  );
};

/**
 * AC9's hover, mounted OUTSIDE the sticky band — and the location is the whole point.
 *
 * design-lead clipped this in their own first render: the chip lives in a sticky band,
 * and the row that got cut was `460 + 582 = 1,042 ✓` — the reconciliation the hover
 * exists to show — while the chip still looked fine. Their fix was "use react-tooltip,
 * it portals to `body`".
 *
 * **It does not, in 5.28.** The portal was v4 behaviour; v5 renders the tooltip where
 * the `<Tooltip>` element is mounted. Mounted inside the picker it renders inside the
 * sticky band and clips exactly as the hand-rolled popover would have — the note was
 * right about the tool and wrong about the mechanism, which is the most expensive
 * combination because the tool change looks like the fix. Caught by asserting the
 * structure (`band.contains(hover) === false`) instead of trusting the claim.
 *
 * So it is mounted at the page root, and anchors bind to it by id from anywhere.
 */
const ChipCoverageTooltip: React.FC<{ rows: RejectionTypeCount[] }> = ({ rows }) => (
  <Tooltip
    id={CHIP_TOOLTIP_ID}
        className="z-50 !rounded-2xl !bg-white !p-3 !opacity-100 !shadow-lg"
        opacity={1}
        render={({ activeAnchor }) => {
          const type = activeAnchor?.getAttribute('data-chip-type');
          const row = rows.find((r) => r.interaction_type === type);
          if (!row || row.reviewable_rejections == null || row.covered_suppressed == null) return null;
          const reconciles = chipReconciles(row);
          return (
            <div data-testid={`chip-hover-${row.interaction_type}`} className="w-[280px] text-ink">
              <p className="text-[12px] font-extrabold">{prettyType(row.interaction_type)}</p>
              <table className="mt-2 w-full text-[12px] tabular-nums">
                <tbody>
                  <tr>
                    <td className="py-0.5 text-inkMuted">total rejected</td>
                    <td className="text-right font-bold">{row.rejections.toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td className="py-0.5 text-inkMuted">shown (reviewable)</td>
                    <td className="text-right font-bold">
                      {row.reviewable_rejections.toLocaleString()}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-0.5 text-inkMuted">suppressed as covered</td>
                    <td className="text-right font-bold">{row.covered_suppressed.toLocaleString()}</td>
                  </tr>
                  <tr className="border-t border-softBorder">
                    <td className="pt-1 font-semibold">
                      {row.reviewable_rejections.toLocaleString()} +{' '}
                      {row.covered_suppressed.toLocaleString()}
                    </td>
                    {/* AC9 — the hover must RECONCILE. Rendered as an arithmetic
                        statement rather than a restated total, so a mismatch is visible
                        as a wrong sum instead of hiding as two plausible numbers. */}
                    <td
                      data-testid="chip-hover-reconciliation"
                      data-reconciles={reconciles ? 'true' : 'false'}
                      className={`pt-1 text-right font-extrabold ${
                        reconciles ? '' : 'text-danger-text'
                      }`}
                    >
                      = {(row.reviewable_rejections + row.covered_suppressed).toLocaleString()}{' '}
                      {reconciles ? '✓' : `≠ ${row.rejections.toLocaleString()}`}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
    }}
  />
);

// ---------------------------------------------------------------------------
// Zero states — AC10. Empty and broken must NOT render alike.
//
// The screen this queue is modelled on already fails this: its catch sets `error`
// without clearing `items`, and its progress pill renders "All caught up" on a FAILED
// first load — broken showing the most reassuring copy on the page. So here every
// empty-state string is gated on `!error`, and no catch falls through to the empty
// component.
//
// Five axes differ, measured by design-lead: border, background, ICON PATH, heading,
// and ACTION. The action axis is the one that matters most — the two states offer
// different things to DO, so they diverge behaviourally rather than decoratively. The
// icon PATH difference is what makes the distinction survive greyscale and CVD, where
// a hue-only difference would not.
// ---------------------------------------------------------------------------

/**
 * AC8 / TRAP-2 — the THIRD zero. "Nothing was ever here" and "everything here was
 * already covered" are both "the queue is empty and that is intended", and design-lead's
 * ruling is the one that separates them honestly: **the only thing that differs is the
 * number.** So all three panels carry the same `rejected · suppressed · shown` triple in
 * the same place, and the eye lands on the one field that changes. Styling alone cannot
 * do this — it is the same reasoning as the type chips one screen up.
 */
const AllSuppressedState: React.FC<{ type: string; coverage: RejectionCoverage }> = ({
  type,
  coverage,
}) => (
  <div
    data-testid="queue-all-suppressed"
    className="rounded-xl border-2 border-preview/40 bg-preview-light p-6 text-center"
  >
    <p className="text-[11px] font-bold uppercase tracking-wider text-preview">
      all suppressed as covered
    </p>
    <p className="mt-3 text-sm font-extrabold">
      Nothing to review — every {prettyType(type)} rejection here was already covered.
    </p>
    {/* feat-206 AC24 — "the same moment" was the seconds rule talking. Coverage is a
        shared STRETCH OF TRANSCRIPT now, and this is the only line on the reviewer's
        screen that explains the rule at all. */}
    <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-inkMuted">
      The annotator kept a different interaction over the same stretch of transcript, so
      none of these rejections dropped anything.
    </p>
    <p data-testid="suppression-triple" className="mt-3 text-xs tabular-nums">
      <span className="font-extrabold text-preview">{coverage.total_in_scope.toLocaleString()}</span>{' '}
      <span className="text-inkMuted">
        rejected · <strong className="text-ink">{coverage.suppressed_covered.toLocaleString()}</strong>{' '}
        suppressed · <strong className="text-ink">{coverage.reviewable_total.toLocaleString()}</strong>{' '}
        shown
      </span>
    </p>
    {/* D4 RULED 2026-08-10 and this line stayed put: AC31 found 605 empty-part-set
        candidates split 364 zero-length / 241 real moments inside one long part, so
        zero-length is suppressed (and counted here) while the 241 stay reviewable and
        are counted nowhere — they are in the queue, where a human can see them.

        feat-206 D11 / D4(a) — the zero-length suppressions, on the one surface where the
        sentence is about the person reading it: they are the human who did not see
        them. **The triple above is a PARTITION and stays exactly three terms** — this
        is a subset of one of them, so it renders below it as its own block, in warning
        tokens, left-aligned, at a smaller size: four differences at once, so it cannot
        be scanned as a fourth term.

        **At 0 and when absent, this queue renders nothing** — deliberately collapsing
        two states the metrics panel keeps apart. That panel is an audit screen where
        "not reported" is itself information; this is a work screen, where a
        zero-diagnostic is noise between a reviewer and their next card. Recorded so it
        reads as a decision rather than an oversight. */}
    {typeof coverage.suppressed_zero_length === 'number' && coverage.suppressed_zero_length > 0 && (
      <span
        data-testid="suppressed-zero-length"
        className="mx-auto mt-2.5 block max-w-md border-l-2 border-warning-solid bg-warning-surface/60 px-2.5 py-1.5 text-left text-[11px] leading-relaxed tabular-nums"
      >
        <strong>
          {coverage.suppressed_zero_length.toLocaleString()} of those were detections with no
          duration
        </strong>{' '}
        — start and end at the same instant, so they were hidden from you without review. That is a
        fault in what the annotator emitted, not a moment someone else covered.
      </span>
    )}
  </div>
);

const EmptyState: React.FC<{
  type: string;
  checkedAt: string;
  onPickAnother: () => void;
  /**
   * AC8 — WHY the queue is empty, decided from the coverage fields rather than from
   * `items.length` (ADR 0001 §4.1), and carried on the DOM so the states are assertable
   * rather than only visible.
   *
   * **`all_suppressed` never reaches this component.** It renders as its own
   * {@link AllSuppressedState} panel, with its own copy and its own
   * `rejected · suppressed · shown` triple — see the branch at the call site. This
   * component handles `nothing_in_scope`, `unknown` and `unreconciled`.
   *
   * (Superseded note, kept so nobody re-derives it: an earlier version of this comment
   * said the S6 panel was "design-gated markup" that had not landed and that the two
   * states "still LOOK alike, which is exactly what AC8 forbids at ship". **That was
   * true when written and went false the moment the panel was built** — the referent
   * moved and nothing in the sentence signalled it. It was the most authoritative text
   * in the file on precisely the question AC8 is adjudicated on, and it was wrong.)
   */
  emptyKind: QueueEmptyKind;
}> = ({ type, checkedAt, onPickAnother, emptyKind }) => (
  <div
    data-testid="queue-empty"
    data-empty-kind={emptyKind}
    className="rounded-xl border border-softBorder bg-white p-6 text-center"
  >
    <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-cream-dark text-inkMuted">
      <List className="h-5 w-5" aria-hidden="true" />
    </div>
    <p className="text-sm font-extrabold">Nothing to review for {prettyType(type)}</p>
    <p className="mx-auto mt-1 max-w-xs text-xs leading-relaxed text-inkMuted">
      The double-check looked and discarded no {prettyType(type)} candidates in this window. This is
      a real answer, not a problem.
    </p>
    {/* "excludes demo servers" is on the count because `or self.demo` auto-confirms
        every type, so a demo `confirmed` is not a judgement — any "reviewed N" that
        includes demo is a false sentence. */}
    <p className="mt-3 text-[11px] text-inkMuted">{checkedAt} · 0 of 0 · excludes demo servers</p>
    <button
      type="button"
      onClick={onPickAnother}
      className="mt-4 rounded-lg border border-softBorder px-3 py-1.5 text-xs font-semibold hover:bg-cream"
    >
      Pick another type
    </button>
  </div>
);

const ErrorState: React.FC<{ detail: string; onRetry: () => void }> = ({ detail, onRetry }) => (
  <div
    data-testid="queue-error"
    role="alert"
    className="rounded-xl border-2 border-danger/30 bg-danger-surface/30 p-6 text-center"
  >
    <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-danger/10 text-danger">
      <AlertCircle className="h-5 w-5" aria-hidden="true" />
    </div>
    <p className="text-sm font-extrabold text-danger-text">Couldn't load the queue</p>
    <p className="mx-auto mt-1 max-w-xs text-xs leading-relaxed text-danger-text/80">
      The request failed, so we don't know whether there's anything to review.{' '}
      <strong>This is not an empty queue.</strong>
    </p>
    <p className="mt-3 font-mono text-[11px] text-danger-text/70">{detail}</p>
    <button
      type="button"
      onClick={onRetry}
      className="mt-4 rounded-lg bg-danger px-3 py-1.5 text-xs font-bold text-white hover:opacity-90"
    >
      Try again
    </button>
  </div>
);

/**
 * End-of-queue is DISTINCT from empty on purpose. "Nothing was here" and "you cleared
 * it" are different moments for someone working an hour a day, and collapsing them
 * throws away the only positive feedback this screen can give her.
 */
const EndOfQueueState: React.FC<{
  type: string;
  reviewed: number;
  overturned: number;
  onNextType: () => void;
  nextType: string | null;
}> = ({ type, reviewed, overturned, onNextType, nextType }) => (
  <div
    data-testid="queue-end"
    className="rounded-xl border border-success/30 bg-success-light/50 p-6 text-center"
  >
    <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-success/15 text-success">
      <Check className="h-5 w-5" aria-hidden="true" />
    </div>
    <p className="text-sm font-extrabold">
      {prettyType(type)} queue cleared — {reviewed} reviewed
    </p>
    <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-inkMuted">
      You kept <strong className="text-ink">{overturned}</strong> the double-check had thrown out.
      Those go to the metrics panel; they don't change anyone's score.
    </p>
    {nextType && (
      <button
        type="button"
        onClick={onNextType}
        className="mt-4 rounded-lg bg-coral px-3 py-1.5 text-xs font-bold text-white"
      >
        Next type: {prettyType(nextType)}
      </button>
    )}
  </div>
);

const LoadingState: React.FC = () => (
  <div data-testid="queue-loading" className="rounded-xl border border-softBorder bg-white p-5">
    <p className="sr-only" role="status" aria-live="polite">
      Loading the queue…
    </p>
    <div className="flex animate-pulse gap-5">
      <div className="min-w-0 flex-1 space-y-2">
        <div className="h-3 w-2/3 rounded bg-cream-dark" />
        <div className="h-3 w-11/12 rounded bg-cream-dark" />
        <div className="h-3 w-3/4 rounded bg-cream-dark" />
      </div>
      <div className="w-[124px] shrink-0 space-y-1.5">
        <div className="h-9 w-full rounded-lg bg-cream-dark" />
        <div className="h-9 w-full rounded-lg bg-cream-dark" />
        <div className="h-9 w-full rounded-lg bg-cream-dark" />
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------

const RecallReviewQueue: React.FC = () => {
  const { interactionType = '' } = useParams<{ interactionType: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const windowDays = parseWindow(searchParams.get('days'));
  const forced = forcedStateFromQuery(searchParams.get('__state'));

  const [items, setItems] = useState<InteractionRejectionItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const [lastDecision, setLastDecision] = useState<{
    judgementId: string;
    shiftId: string;
    verdict: RecallVerdict;
    previous: RecallVerdict | null;
    undone: boolean;
  } | null>(null);
  const [checkedAt, setCheckedAt] = useState('');
  const [coverage, setCoverage] = useState<RejectionCoverage | null>(null);
  /** AC2 — which cards have their ONE disclosure open. */
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  /**
   * AC3 — decided cards the reviewer asked to see again. Session state on purpose: a
   * reopen is "I want to look at this now", not a persisted property of the row, and
   * persisting it would make the collapsed trail grow back on every reload.
   */
  const [reopenedIds, setReopenedIds] = useState<Set<string>>(() => new Set());

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const reopen = useCallback((id: string) => {
    setReopenedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);
  const [typeCounts, setTypeCounts] = useState<RejectionTypeCount[] | null>(null);
  const [countsFailed, setCountsFailed] = useState(false);

  // ADR §0.3.2 — latency is measured from CARD ACTUALLY RENDERED, not from fetch
  // start. A slow network would otherwise manufacture plausible-looking latencies,
  // which is worse than no measurement: it would populate the implausibly-fast
  // distribution with numbers that describe the network rather than the reviewer.
  const shownAtRef = useRef<Map<string, number>>(new Map());
  const markShown = useCallback((id: string) => {
    if (!shownAtRef.current.has(id)) shownAtRef.current.set(id, Date.now());
  }, []);

  /**
   * An out-of-set type is a ROUTING error resolved before any fetch — never a 422 to
   * classify after the fact. `drink_promotion` is auto-confirmed before any model
   * call, so it can never have a rejection; a stale bookmark to it must say so rather
   * than render "the request failed", which would be false (nothing failed).
   */
  const typeIsReviewable = isRejectionReviewType(interactionType);

  const load = useCallback(async () => {
    if (!typeIsReviewable) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      if (forced === 'error') throw new Error('forced error state (non-prod ?__state=error)');
      const resp =
        forced === 'empty' || forced === 'end_of_queue'
          ? { items: [] as InteractionRejectionItem[], coverage: undefined }
          : forced === 'all_suppressed'
          ? { items: [] as InteractionRejectionItem[], coverage: FORCED_ALL_SUPPRESSED_COVERAGE }
          : await getInteractionRejections({
              interactionType,
              days: windowDays,
              limit: PAGE_SIZE,
            });
      setItems(resp.items ?? []);
      // Absent stays absent — never coerced to a zeroed object. "The API doesn't
      // compute coverage" and "coverage found nothing" are different facts, and only
      // one of them licenses saying anything about suppression.
      setCoverage(resp.coverage ?? null);
      setFocusIndex(0);
    } catch (e) {
      Sentry.captureException(e);
      // AC10 — a catch NEVER falls through to the empty component. `error` is set and
      // every empty-state string below is gated on `!error`.
      setError(e instanceof Error ? e.message : 'Failed to load the queue');
    } finally {
      setIsLoading(false);
      setCheckedAt(new Date().toLocaleString());
    }
  }, [typeIsReviewable, interactionType, windowDays, forced]);

  useEffect(() => {
    load();
  }, [load]);

  // Counts are fetched INDEPENDENTLY of the queue: a failure here must degrade the
  // picker's counts, never the queue itself, and must never render as a zero — "—"
  // and "0" are different facts and only one of them is a measurement.
  useEffect(() => {
    let cancelled = false;
    getRejectionTypeCounts({ days: windowDays })
      .then((resp) => {
        if (cancelled) return;
        setTypeCounts(resp.counts ?? []);
        setCountsFailed(false);
      })
      .catch((e) => {
        if (cancelled) return;
        Sentry.captureException(e);
        setTypeCounts(null);
        setCountsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [windowDays]);

  const decide = useCallback(
    async (item: InteractionRejectionItem, verdict: RecallVerdict) => {
      // feat-206 (design, 2026-08-09) — **A CARD THAT SHOULD NOT BE IN THE QUEUE MUST
      // NOT ACCEPT A VERDICT.** This is not cosmetic: the verdict is written to
      // `recall_review` and reaches the PUBLISHED OVERTURN RATE, so a decision recorded
      // on a row that the predicate should have suppressed corrupts a number that gets
      // quoted. `unsure` is not an escape hatch either — it records a verdict, landing
      // in `covered_unsure` and in the reviewed denominator, on a row that should not
      // be in the population at all.
      //
      // **Guarded HERE rather than on the buttons**, because `decide` is the single
      // chokepoint both paths run through: the click column and the keyboard shortcut
      // at `VERDICT_BY_KEY` (`:1528`) call this same function. Disabling the buttons
      // alone would leave `r`/`s`/`t` writing happily — a half-fix that looks like a
      // whole one, which is worse than none.
      //
      // `captureException`, not `captureMessage`: several suites mock `@sentry/react`
      // as `{ captureException }` only, so the other one is a TypeError at test time.
      if (hasSharingNeighbour(item)) {
        Sentry.captureException(
          new Error(
            `feat-206: verdict "${verdict}" blocked on judgement ${item.judgement_id} — a retained ` +
              'neighbour reports shares_part=true, so the coverage predicate and the neighbour ' +
              'annotation disagree. This card should have been suppressed.',
          ),
        );
        return;
      }
      const previous = item.review?.verdict ?? null;
      const shownAt = shownAtRef.current.get(item.judgement_id);
      // Omit when unmeasured; send when measured, INCLUDING a measured 0. NULL and 0
      // are different facts and must stay distinguishable.
      const latency = shownAt != null ? Math.max(0, Date.now() - shownAt) : null;

      setPendingId(item.judgement_id);
      // Optimistic, rolled back on failure — never a silent success.
      setItems((prev) =>
        prev.map((i) =>
          i.judgement_id === item.judgement_id
            ? { ...i, review: { verdict, reviewed_by: null, reviewed_at: null, created: null, decision_latency_ms: latency } }
            : i,
        ),
      );
      try {
        await putRecallVerdict(item.judgement_id, verdict, latency);
        setLastDecision({
          judgementId: item.judgement_id,
          shiftId: item.shift_id,
          verdict,
          previous,
          undone: false,
        });
        if (ADVANCE_ON_DECISION) setFocusIndex((n) => n + 1);
      } catch (e) {
        Sentry.captureException(e);
        setItems((prev) => prev.map((i) => (i.judgement_id === item.judgement_id ? item : i)));
        setError(e instanceof Error ? e.message : 'Failed to record the verdict');
      } finally {
        setPendingId(null);
      }
    },
    [],
  );

  const undo = useCallback(async () => {
    if (!lastDecision) return;
    setPendingId(lastDecision.judgementId);
    try {
      // Restores the PRIOR state including never-decided: `previous == null` clears
      // the row rather than writing a stand-in. A written `unsure` would be
      // indistinguishable from one she chose, and `unsure` has its own published rate.
      const { changed } = await undoRecallVerdict(lastDecision.judgementId, lastDecision.previous);
      setItems((prev) =>
        prev.map((i) =>
          i.judgement_id === lastDecision.judgementId
            ? {
                ...i,
                review: lastDecision.previous
                  ? { verdict: lastDecision.previous, reviewed_by: null, reviewed_at: null, created: null, decision_latency_ms: null }
                  : null,
              }
            : i,
        ),
      );
      // The strip does NOT clear — if it did, the only evidence a write-and-unwrite
      // happened would vanish, and an Undo can itself be a mis-click. `changed:false`
      // means there was nothing to clear, so don't claim we undid something.
      if (changed) setLastDecision({ ...lastDecision, undone: true });
    } catch (e) {
      Sentry.captureException(e);
      setError(e instanceof Error ? e.message : 'Failed to undo');
    } finally {
      setPendingId(null);
    }
  }, [lastDecision]);

  const goBack = useCallback(() => setFocusIndex((n) => Math.max(0, n - 1)), []);

  /**
   * feat-205 AC5a — keyboard grammar, matching the feat-159 page's SHAPE.
   *
   * This screen has **zero** bindings today against the reference page's fourteen, so
   * this is net-new rather than a port — the spec's "matches the reference page's
   * grammar" would otherwise send an implementer hunting for bindings to preserve
   * (design-lead's positive control, recorded by product-lead as AC5a).
   *
   * Inherited from the reference page verbatim: `J`/`K` and the arrows for movement,
   * modifier keys ignored, and NEVER firing while a form control has focus. Deviating
   * on the decision letters only, for the `K` collision — see VERDICT_SLOTS.
   *
   * Read through a ref so the listener can be registered ONCE with no deps: a handler
   * re-registered on every render would still work, but it makes the number of live
   * listeners a function of render count, and this component re-renders per keypress.
   */
  const cleared = forced === 'end_of_queue' || (items.length > 0 && focusIndex >= items.length);
  const kb = useRef({
    items,
    focusIndex,
    busy: false,
    // `active` gates on the LIST being on screen. Without it, movement keys pressed on
    // the end-of-queue or error state would silently rewind `focusIndex` and re-render
    // a list the reviewer is no longer looking at.
    active: false,
    decide,
    toggleExpand,
    reopen,
    reopenedIds,
  });
  kb.current = {
    items,
    focusIndex,
    busy: pendingId != null,
    active: typeIsReviewable && !isLoading && !error && items.length > 0 && !cleared,
    decide,
    toggleExpand,
    reopen,
    reopenedIds,
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || target?.isContentEditable) {
        return;
      }
      const { items: rows, focusIndex: idx, busy, active } = kb.current;
      if (!active) return;
      const key = e.key;
      if (key === 'j' || key === 'J' || key === 'ArrowDown') {
        e.preventDefault();
        setFocusIndex((n) => Math.min(n + 1, rows.length - 1));
        return;
      }
      if (key === 'k' || key === 'K' || key === 'ArrowUp') {
        e.preventDefault();
        setFocusIndex((n) => Math.max(n - 1, 0));
        return;
      }
      if (key === 'Enter' || key === ' ') {
        // Buttons and links activate natively on Enter/Space — don't double-fire.
        if (tag === 'BUTTON' || tag === 'A') return;
        const row = rows[idx];
        if (!row) return;
        e.preventDefault();
        // The reference page's grammar: on a COLLAPSED row the expand affordance is
        // "reopen" (there is no disclosure to open on a thin row); on an open card it
        // is the disclosure. Same key, because to the reviewer both mean "show me more
        // of this one".
        const isCollapsed = row.review != null && !kb.current.reopenedIds.has(row.judgement_id);
        if (isCollapsed) kb.current.reopen(row.judgement_id);
        else kb.current.toggleExpand(row.judgement_id);
        return;
      }
      const verdict = key.length === 1 ? VERDICT_BY_KEY[key.toLowerCase()] : undefined;
      if (verdict && !busy) {
        const row = rows[idx];
        if (!row) return;
        e.preventDefault();
        // Same `decide` the click column calls, deliberately — the latency instrument
        // (AC6) and the optimistic write live in there, and a second decision path
        // would be a second place for them to be missing from.
        kb.current.decide(row, verdict);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const decidedCount = useMemo(() => items.filter((i) => i.review != null).length, [items]);
  const overturnedCount = useMemo(
    () => items.filter((i) => i.review?.verdict === 'overturned').length,
    [items],
  );
  const emptyKind = deriveEmptyKind(coverage);
  const nextType = REJECTION_REVIEW_TYPES.find((t) => t !== interactionType) ?? null;
  const goToType = useCallback(
    (t: string) => {
      if (!t) return;
      navigate(`/restaurant/internal/interaction-evaluations/rejections/${t}?days=${windowDays}`);
    },
    [navigate, windowDays],
  );

  // The picker's fallback rows, hoisted so the tooltip and the chips read the SAME
  // list — a tooltip built from a different array than the chips is how a hover ends
  // up describing the wrong type.
  const chipRows: RejectionTypeCount[] =
    typeCounts ??
    REJECTION_REVIEW_TYPES.map((t) => ({
      interaction_type: t,
      rejections: -1,
      state: 'in_phase_1',
      reviewable: true,
    }));

  return (
    <div className="min-h-screen-dynamic bg-cream">
      <ScorecardNavigation />
      {/* Mounted at the page root, OUTSIDE the sticky band — see ChipCoverageTooltip.
          react-tooltip 5 renders in place, so this position is load-bearing, not
          incidental. */}
      <ChipCoverageTooltip rows={chipRows} />
      <div className="mx-auto max-w-5xl px-4 py-6 md:px-8">
        <h1 className="mb-1 text-2xl font-extrabold">Recall QA — reviewer queue</h1>
        <p className="mb-4 text-sm text-inkMuted">
          Candidates the live double-check threw away. Your call is eval metadata only — it changes
          no score, no XP and no leaderboard.
        </p>

        {/* Type picker. drink_promotion is ABSENT: it is auto-confirmed before any
            model call, so its queue is empty FOREVER, not empty today. Offering it
            would spend a click on a screen that can never have anything.

            STICKY, matching the reference page's band. `top-24` (96px), NOT a smaller
            offset: feat-151 measured the real nav at 85.5px and deliberately moved this
            family to top-24 to clear it with ~10px of margin for its height variance.
            Under-clearance tucks the bar under the nav — a functional break — while
            over-clearance costs a few px of scroll, so this biases high.

            NOT `overflow-hidden`: the AC9 chip hover is anchored in here, and design-lead
            clipped it in their own first render — the row that got cut was the
            reconciliation the hover exists to show, and the chip still looked fine. */}
        <div className="sticky top-24 z-30 mb-3 rounded-t-xl border border-softBorder bg-white/95 px-4 py-3 backdrop-blur">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-wide text-inkMuted">Type</span>
            <TypePicker
              counts={typeCounts}
              countsFailed={countsFailed}
              current={interactionType}
              windowDays={windowDays}
            />
            {items.length > 0 && (
              <span
                data-testid="queue-progress"
                className="ml-auto flex items-center gap-2 text-[12px] font-semibold text-inkMuted"
              >
                <span className="tabular-nums">
                  {decidedCount} of {items.length} cleared
                </span>
                <span className="h-1.5 w-28 overflow-hidden rounded-full bg-cream-dark">
                  <span
                    data-testid="queue-progress-bar"
                    className="block h-full rounded-full bg-success"
                    style={{ width: `${Math.round((decidedCount / items.length) * 100)}%` }}
                  />
                </span>
              </span>
            )}
          </div>
          {/* AC5a — the legend and the bindings read the SAME source (`VERDICT_SLOTS`),
              so a letter cannot be changed in one and not the other. */}
          <p
            data-testid="shortcut-legend"
            className="mt-2 rounded bg-cream/70 px-2 py-1 font-mono text-[11px] leading-relaxed text-inkMuted"
          >
            Shortcuts: J/K or ↑/↓ move ·{' '}
            {VERDICT_SLOTS.map((s) => `${s.key.toUpperCase()} ${SHORTCUT_GLOSS[s.verdict]}`).join(' · ')} ·
            Enter expand
          </p>
          {/* ORDER DISCLOSURE — required. The queue is a stable seeded shuffle, so a
              partly-finished queue is still a fair sample. design-lead's copy: the
              mechanism is OUR reason; "stopping partway is fine" is HER reason, and
              it's the sentence that changes behaviour. */}
          <p className="mt-2 flex items-center gap-1.5 border-t border-softBorder pt-2 text-xs text-inkMuted">
            <Shuffle className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span data-testid="order-disclosure">
              <strong className="text-ink">Fixed random order</strong> — not newest first.{' '}
              <strong className="text-ink">Stopping partway is fine.</strong>
            </span>
            <span
              className="cursor-help underline decoration-dotted"
              title="Cards are shuffled with a constant seed, so whatever portion you finish is a fair sample of the whole queue rather than just the most recent rows. The order never changes between visits."
            >
              why?
            </span>
          </p>
        </div>

        {!typeIsReviewable ? (
          // The THIRD zero-state: not empty, not broken — structurally impossible.
          <div data-testid="queue-never-discarded" className="rounded-xl border border-softBorder bg-white p-6 text-center">
            <p className="text-sm font-extrabold">{prettyType(interactionType)} is always auto-confirmed</p>
            <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-inkMuted">
              The double-check auto-confirms this type before any model call, so it is{' '}
              <strong className="text-ink">never discarded</strong> — there is nothing here to
              review, and there never will be.
            </p>
          </div>
        ) : isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState detail={error} onRetry={load} />
        ) : items.length === 0 ? (
          // AC8 — WHICH zero, decided from the coverage FIELDS and not from
          // `items.length`, which is 0 in every one of these cases.
          emptyKind === 'all_suppressed' && coverage ? (
            <AllSuppressedState type={interactionType} coverage={coverage} />
          ) : (
            <EmptyState
              type={interactionType}
              checkedAt={checkedAt}
              onPickAnother={() => goToType(nextType ?? '')}
              emptyKind={emptyKind}
            />
          )
        ) : cleared ? (
          <EndOfQueueState
            type={interactionType}
            reviewed={decidedCount}
            overturned={overturnedCount}
            nextType={nextType}
            onNextType={() => goToType(nextType ?? '')}
          />
        ) : (
          <div className="mb-2 divide-y divide-softBorder rounded-b-xl border border-t-0 border-softBorder bg-white">
            {items.map((item, idx) => (
              <RejectionCard
                key={item.judgement_id}
                item={item}
                isFocused={idx === focusIndex}
                busy={pendingId === item.judgement_id}
                strip={
                  idx === focusIndex && lastDecision && lastDecision.judgementId !== item.judgement_id
                    ? lastDecision
                    : null
                }
                expanded={expandedIds.has(item.judgement_id)}
                reopened={reopenedIds.has(item.judgement_id)}
                onShown={markShown}
                onDecide={(v) => decide(item, v)}
                onUndo={undo}
                onToggleExpand={() => toggleExpand(item.judgement_id)}
                onReopen={() => reopen(item.judgement_id)}
                onBack={idx === focusIndex && focusIndex > 0 ? goBack : undefined}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const VERDICT_PILL: Record<RecallVerdict, { label: string; pill: string; row: string }> = {
  upheld: {
    label: 'Double-check was right',
    pill: 'bg-success text-white',
    row: 'bg-success-light',
  },
  overturned: {
    label: 'Should have been kept',
    pill: 'bg-danger text-white',
    row: 'bg-danger-surface',
  },
  // `danger` has no `-light`; `danger-surface` is the fill token. The neutral row
  // stays white — a third tint would imply a third judgement.
  unsure: {
    label: "Can't tell from this",
    pill: 'border border-softBorder text-inkMuted',
    row: 'bg-white',
  },
};

/**
 * AC3 — the decided card, collapsed IN PLACE.
 *
 * Verdict + the opening of the transcript + a reopen affordance, and nothing else. It
 * is rendered INSIDE the same `<section>` as the open card, never as a sibling that
 * replaces it — see the note on the card below for why that is load-bearing.
 */
const CollapsedDecided: React.FC<{
  item: InteractionRejectionItem;
  verdict: RecallVerdict;
  onReopen: () => void;
}> = ({ item, verdict, onReopen }) => {
  const pill = VERDICT_PILL[verdict];
  const opening = (item.transcript ?? [])
    .slice(0, 2)
    .map((p) => p.text)
    .join(' ');
  return (
    <div
      data-testid="collapsed-decided"
      className={`flex items-center gap-3 rounded-xl border border-softBorder px-4 py-2.5 ${pill.row}`}
    >
      <span
        data-testid="collapsed-verdict"
        className={`shrink-0 rounded-md px-2 py-0.5 text-[11px] font-extrabold ${pill.pill}`}
      >
        {pill.label}
      </span>
      <span data-testid="collapsed-opening" className="min-w-0 flex-1 truncate text-[13px] text-inkMuted">
        {opening ? `“${opening}”` : <span className="italic">no transcript in the window</span>}
      </span>
      <button
        type="button"
        onClick={onReopen}
        data-testid="reopen-button"
        className="shrink-0 text-[12px] font-semibold text-coral hover:underline"
      >
        Reopen
      </button>
    </div>
  );
};

const RejectionCard: React.FC<{
  item: InteractionRejectionItem;
  isFocused: boolean;
  busy: boolean;
  strip: { shiftId: string; verdict: RecallVerdict; undone: boolean } | null;
  expanded: boolean;
  reopened: boolean;
  onShown: (id: string) => void;
  onDecide: (v: RecallVerdict) => void;
  onUndo: () => void;
  onToggleExpand: () => void;
  onReopen: () => void;
  onBack?: () => void;
}> = ({
  item,
  isFocused,
  busy,
  strip,
  expanded,
  reopened,
  onShown,
  onDecide,
  onUndo,
  onToggleExpand,
  onReopen,
  onBack,
}) => {
  // LAYOUT effect, not a passive one, and the distinction is load-bearing.
  //
  // `decision_latency_ms` is measured from the card being RENDERED. A passive
  // `useEffect` runs asynchronously AFTER paint, so there is a window in which the
  // card is on screen and clickable but has no timestamp — and a click landing in
  // that window sends `null` ("no measurement") instead of a real latency.
  //
  // That is not merely a lost sample, it is a BIASED one: the window is at most a
  // frame or two, so the only decisions that can fall in it are the FASTEST ones —
  // exactly the population `implausibly_fast_rate` exists to measure. Losing samples
  // preferentially from the fast tail makes that fraction read LOW, i.e. it would
  // under-report mis-clicks, which is the failure the metric was added to catch.
  //
  // `useLayoutEffect` runs synchronously after commit and BEFORE paint, so the
  // invariant becomes structural: **if the card can be seen, it carries a
  // timestamp.** The service's `null` path stays — it is the correct contract for
  // "no measurement was taken" and is still exercised by Undo, which deliberately
  // sends none — but this component should never be the thing producing it.
  useLayoutEffect(() => {
    onShown(item.judgement_id);
  }, [item.judgement_id, onShown]);

  const verdict = item.review?.verdict ?? null;
  // AC3 — decided cards collapse in place. `reopened` is session state: the reviewer
  // asked to see this one again, and it stays open until the page reloads.
  const collapsed = verdict != null && !reopened;

  return (
    // ONE `<section>` per judgement, for every state this card can be in.
    //
    // The collapsed row is rendered INSIDE it rather than as a sibling that replaces
    // it, so decide → collapse → reopen is a same-node transition. design-lead's
    // approved mockup calls for that, and the reason recorded there was originally
    // wrong (a remount cannot restart the clock — `shownAtRef` is parent-owned and
    // write-once, `:687-690`). The corrected reason is the one that matters and it is
    // still a reason: **the invariant Part A must not break is that the clock stays
    // parent-owned and write-once.** Move that Map into this component and a remount
    // WOULD re-arm it, which manufactures a second, tiny latency for a decision that
    // already happened — and AC6's own control would not catch it, because the clock
    // would still be armed before paint.
    <section
      data-testid="rejection-card"
      data-judgement-id={item.judgement_id}
      data-verdict={verdict ?? 'none'}
      data-collapsed={collapsed ? 'true' : 'false'}
      data-focused={isFocused ? 'true' : 'false'}
      className="p-3"
    >
      {strip && <LastDecisionStrip state={strip} busy={busy} onUndo={onUndo} />}
      {collapsed ? (
        <CollapsedDecided item={item} verdict={verdict as RecallVerdict} onReopen={onReopen} />
      ) : (
        <div
          className={`rounded-2xl border bg-white p-4 shadow-sm ${
            isFocused ? 'border-2 border-coral/40' : 'border-softBorder'
          }`}
        >
          {/*
            AC1 — the card's FACE is the transcript and the three buttons. What used to
            be here and is now gone: the shift UUID, the prompt UUID, the server and
            location, a `TRANSCRIPT WINDOW` heading, an `ANNOTATOR'S OWN PIPELINE`
            heading, the excerpt printed twice, and a ~120-word machine rationale. The
            reviewer is asked "was the double-check right?" — so the double-check's own
            argument cannot be the largest object on screen.

            Two affordances survive: `View shift ↗` (the reference page's only header
            control) and ONE disclosure. Not two — AC2 says all pipeline detail is
            reachable from a single collapsed control, and nesting a second expander
            inside would satisfy "reachable" while restoring the density this feature
            exists to remove.
          */}
          <div className="mb-3 flex items-center justify-between">
            {item.server_id ? (
              <Link
                to={`/restaurant/shifts/${item.shift_id}/transcript/${item.server_id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="view-shift-link"
                className="text-[12px] font-semibold text-coral hover:underline"
                title="Open the full shift transcript in a new tab"
              >
                View shift ↗
              </Link>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={onToggleExpand}
              aria-expanded={expanded}
              data-testid="ai-evidence-toggle"
              className={`text-[12px] ${expanded ? 'font-semibold text-ink' : 'text-inkMuted hover:text-ink'}`}
            >
              {expanded ? 'AI evidence ⌃' : 'AI evidence ⌄'}
            </button>
          </div>
          <div className="flex gap-4">
            <div className="min-w-0 flex-1">
              <TranscriptWindow item={item} />
              {expanded && <AnnotatorPipeline item={item} />}
            </div>
            <ClickColumn
              verdict={verdict}
              busy={busy}
              onDecide={onDecide}
              onBack={onBack}
              backDisabled={busy}
              blocked={hasSharingNeighbour(item)}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default RecallReviewQueue;
